import React from 'react';
import { useLocalUrl } from '../../src/lib/hooks/useLocalUrl';
import { Package } from 'lucide-react';

interface SmartImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  imageUrl: string | null | undefined;
  fallback?: React.ReactNode;
  /** Background shown behind the image (e.g. in object-contain letterbox gaps) and on the placeholder/loading states. Defaults to the existing gray-50. */
  bgClassName?: string;
}

/**
 * SmartImage: Optimized for instant feedback.
 * Shows a placeholder immediately and swaps for the image when resolved.
 * No indefinite spinners or complex timeouts.
 */
export const SmartImage: React.FC<SmartImageProps> = ({ imageUrl, fallback, bgClassName = 'bg-gray-50', ...props }) => {
  const resolvedUrl = useLocalUrl(imageUrl);
  const [hasError, setHasError] = React.useState(false);
  const [isBrowserLoading, setIsBrowserLoading] = React.useState(!!imageUrl);

  // Reset states when URL changes
  React.useEffect(() => {
    setHasError(false);
    setIsBrowserLoading(!!imageUrl);
  }, [imageUrl, resolvedUrl]);

  const renderPlaceholder = () => {
    if (fallback) return <>{fallback}</>;
    return (
      <div className={`${props.className || ''} ${bgClassName} flex items-center justify-center border border-gray-100`}>
        <div className="flex flex-col items-center gap-1 opacity-20">
          <Package size={16} className="text-gray-400" />
        </div>
      </div>
    );
  };

  // 1. If no image URL provided or we already hit max retries, show placeholder
  if (!imageUrl || resolvedUrl === null || hasError) {
    return renderPlaceholder();
  }

  return (
    <div key={resolvedUrl || 'pending'} className={`relative ${props.className || ''} ${bgClassName} overflow-hidden flex items-center justify-center`}>
      {/* 2. Show a static placeholder icon behind the image while it's loading */}
      {isBrowserLoading && (
        <div className="absolute inset-0 flex items-center justify-center opacity-10">
          <Package size={16} />
        </div>
      )}

      {/* Search-sourced images may reject requests carrying our app's referrer. */}
      {resolvedUrl && (
        <img
          src={resolvedUrl}
          referrerPolicy="no-referrer"
          {...props}
          className={`w-full h-full transition-opacity duration-300 ${isBrowserLoading ? 'opacity-0' : 'opacity-100'} ${props.className || 'object-cover'}`}
          ref={(el) => {
            // Cached images fire load synchronously before React attaches onLoad.
            // Check complete immediately after mount to catch that case.
            if (el?.complete && el.naturalWidth > 0) {
              setIsBrowserLoading(false);
            }
          }}
          onLoad={() => {
            setHasError(false);
            setIsBrowserLoading(false);
          }}
          onError={() => {
            setHasError(true);
            setIsBrowserLoading(false);
          }}
        />
      )}
    </div>
  );
};
